import { Children } from "react";
import { Field, FormGroup } from "../../../components/form";
import * as Yup from "yup";

function calculateDisabledProp(context: any, model: any): boolean {
  return context.disabled || model.doesChildAttend == false || model.doesChildAttend == undefined;
}

function calculateValue(model: any, valueInstance: any, returnValue: any): any {
  if (model.doesChildAttend == false || model.doesChildAttend == undefined) {
    return returnValue;
  }
  return valueInstance;
}

function generateFormFields() {
  const daysOfTheWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const formgroup: FormGroup[] = [];

  daysOfTheWeek.map((day) => {
    formgroup.push({
      type: "formGroup",
      key: day,
      customWrapper: ({ children }: any) => (
        <>
          <td>{day}</td>
          {Children.map(children, (child) => (
            <td>{child}</td>
          ))}
        </>
      ),
      fields: dayForm,
    });
  });

  return formgroup;
}

const dayForm: Field[] = [
  {
    type: "field",
    key: "doesChildAttend",
    fieldType: "checkbox",
  },
  {
    type: "field",
    key: "startTime",
    fieldType: "time",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.startTime, null);
    },
    validation: Yup.date().when(["doesChildAttend", "endTime"], ([doesChildAttend, endTime], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      if (endTime == null) {
        return schema.required("A response is required");
      } else {
        return schema.required("A response is required").max(endTime, "The start time may not be later than the end time");
      }
    }),
  },
  {
    type: "field",
    key: "endTime",
    fieldType: "time",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.endTime, null);
    },
    validation: Yup.date().when(["doesChildAttend"], ([doesChildAttend], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      return schema.required("A response is required");
    }),
  },
  {
    type: "field",
    key: "groupNames",
    fieldType: "textarea",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.groupNames, "");
    },
    validation: Yup.string().when(["doesChildAttend"], ([doesChildAttend], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      return schema.required("A response is required");
    }),
  },
  {
    type: "field",
    key: "childrenAttendance",
    fieldType: "number",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.childrenAttendance, "");
    },
    validation: Yup.number().when(["doesChildAttend"], ([doesChildAttend], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      return schema
        .required("A response is required")
        .min(1, "At least one child must be in the group")
        .max(100, "Maximum of 100 children allowed in a group");
    }),
  },
  {
    type: "field",
    key: "advisory",
    fieldType: "advisory",
    label: "*",
  },
];

export const groupInformationForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      label:
        "Please provide information about the hours the child attends on each day of the week and the group name for each day. If there are rotational groups please note all groups that attend on that day.",
    },
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "warning",
        Body: () => (
          <>
            <p className="list-none pl-2 m-0">
              If multiple KIS applications are received from this kindergarten, this information is used to map which children attend the service at
              the same time as this child. Please ensure that the group names you use are consistent across the service, or we may be required to
              contact you for additional information which can delay processing.
            </p>
          </>
        ),
      },
      label: "",
    },
    {
      type: "formGroup",
      key: "groupInformationWeeklyData",
      customWrapper: ({ children }: any) => (
        <table className="group-info">
          <th></th>
          <th>Does the child attend?</th>
          <th>Start time</th>
          <th>End time</th>
          <th>Group name/s that attend this day, please use a comma (,) to separate the group names</th>
          <th>How many children in total attend this group session on this day</th>
          <th></th>
          {Children.map(children, (child) => (
            <tr>{child}</tr>
          ))}
        </table>
      ),
      fields: generateFormFields(),
    },
    {
      type: "field",
      key: "atleastOneDayOfTheWeek",
      fieldType: "error",
      validation: Yup.boolean().test("atleastOneDayOfTheWeek", "A response is required", function () {
        const { groupInformationWeeklyData } = this.parent;
        return (
          groupInformationWeeklyData.Monday.doesChildAttend ||
          groupInformationWeeklyData.Tuesday.doesChildAttend ||
          groupInformationWeeklyData.Wednesday.doesChildAttend ||
          groupInformationWeeklyData.Thursday.doesChildAttend ||
          groupInformationWeeklyData.Friday.doesChildAttend ||
          groupInformationWeeklyData.Saturday.doesChildAttend ||
          groupInformationWeeklyData.Sunday.doesChildAttend
        );
      }),
    },
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "mt-5 mb-2",
      },
      label: "Staff to Child Ratio",
    },

    {
      type: "formGroup",
      classNameOpt: "staffRatio",
      fields: [
        {
          type: "field",
          fieldType: "number",
          key: "staffAmt",
          label: "Staff",
          props: {
            class: "p-inputtext p-component",
          },
          validation: Yup.number()
            .required("A response is required")
            .max(100, "Staff number must be between 1 and 100")
            .min(1, "Staff number must be between 1 and 100"),
        },
        {
          type: "field",
          fieldType: "number",
          key: "childrenAmt",
          label: "Children",
          validation: Yup.number()
            .required("A response is required")
            .max(100, "Children number must be between 1 and 100")
            .min(1, "Children number must be between 1 and 100"),
        },
      ],
    },
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "fieldadv",
      },
      label:
        "Show the ratio of staff (teachers and educators) to children in the room e.g.: 1 staff to 11 children. Note: Additional Assistants are not counted as staff for this ratio.",
    },
    {
      type: "field",
      fieldType: "radio",
      key: "doesRotationalGroups",
      label: "Does the program run rotational groups?",
      extendedLabel: (
        <p className="list-none pl-2 m-0">
          A rotational model is where a number of smaller sub-groups are brought together to create a larger group. Each child is allocated with other
          children to a small group. These children always attend together throughout the week and will attend with other groups during the week.
          <br />
          Factsheets about the rotational model are available on the department’s{" "}
          <a
            href="https://www.education.vic.gov.au/Documents/childhood/providers/edcare/change-management-info-sheets-your-guide-to-rotational-models.pdf"
            target="_blank"
            rel="noreferrer">
            website<i className="pi pi-external-link ml-1"></i>
          </a>
          .
        </p>
      ),
      validation: Yup.string().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 0,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "radio",
      label: "Do these group/s operate bush kinder sessions during the year?",
      extendedLabel: (
        <p className="list-none pl-2 m-0">
          If the kinder currently operates bush kinder sessions for this group/s, or is planning to during the year, you will be required to upload a
          Bush Kinder Risk Minimisation Plan at the end of this application.
          <br />
          Bush kinder includes beach kinder and kinder programs delivered in other outdoor settings external to the kindergarten, as part of a “bush
          kinder” program.
        </p>
      ),
      key: "doesBushKinderSessions",
      validation: Yup.string().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No/Unsure",
            value: 0,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "advisory",
      key: "advisory",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
      label: (
        <>
          <label>Over what period does bush kinder operate?</label>
        </>
      ),
    },
    {
      type: "field",
      fieldType: "calendar",
      key: "startDate",
      label: "Start Date",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
      validation: Yup.date().when(["endDate"], ([endDate], schema) => {
        if (endDate) {
          return schema.required("A response is required").max(endDate, "The start date must be earlier than the end date");
        }
        return schema.required("A response is required");
      }),
    },
    {
      type: "field",
      fieldType: "calendar",
      key: "endDate",
      label: "End date",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
      validation: Yup.date().required("A response is required"),
    },
    {
      type: "field",
      fieldType: "textarea",
      props: {
        rows: 5,
        col: 30,
        autoResize: true,
      },
      key: "infoOfBushKinderSessions",
      label:
        "If there is any other information about the operation of bush kinder sessions that is important for the KIS assessment team to understand, please include it here.",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
    },
    {
      type: "field",
      fieldType: "radio",
      key: "hasOtherSupport",
      label: "Has your kindergarten group been allocated KIS support from another application/s for this group this year?",
      validation: Yup.string().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 0,
          },
        ],
      },
    },
    {
      type: "formGroup",
      label: "What KIS support has been allocated to this year? Select all that apply.",
      key: "kisSupport",
      hideExpression: (formContext, values) => !values.hasOtherSupport,
      fields: [
        {
          type: "field",
          fieldType: "checkbox",
          key: "specialistConsultancy",
          label: "Specialist consultancy",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "additionalAssistant",
          label: "Additional assistant",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "specialistTraining",
          label: "Specialist training",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "minorBuildingModifications",
          label: "Minor building modifications",
        },
      ],
      test: [
        "atleastOneKisSupport",
        "A response is required",
        (value) => value.minorBuildingModifications || value.specialistTraining || value.additionalAssistant || value.specialistConsultancy,
      ],
    },
    {
      type: "formGroup",
      label: "What supports are your kindergarten group accessing from other programs this year? Select all that apply.*",
      key: "otherSupport",
      fields: [
        {
          type: "field",
          key: "schoolReadinessFunding",
          fieldType: "checkbox",
          label: "School Readiness Funding (SFR)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "flexibleSupportPackages",
          label: "Flexible Support Packages (FSP)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "inclusionSupportPrograms",
          label: "Inclusion Support Programs (ISP)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "preschoolFieldOfficer",
          label: "Preschool Field Officer (PSFO)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "earlyChildhoodInterventionServices",
          label: "Early Childhood Intervention Services (ECIS)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "earlyChildhoodLearningAdvisors",
          label: "LOOKOUT Early Childhood Learning Advisors (ECLAs)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "accessToEarlyLearning",
          label: "Access to Early Learning (EAL)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "mentalHealthInEarlyChildhood",
          label: "Mental Health in Early Childhood (HEC) Program / Early Childhood Mental Health Consultation",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "supportFromOtherProgramsOther",
          label: "Other - please specify",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "supportFromOtherPrograms",
          label: "None of the above",
        },
      ],
      test: [
        "atleastOneOtherSupport",
        "A response is required",
        (value) =>
          value.supportFromOtherPrograms ||
          value.supportFromOtherProgramsOther ||
          value.mentalHealthInEarlyChildhood ||
          value.accessToEarlyLearning ||
          value.earlyChildhoodLearningAdvisors ||
          value.preschoolFieldOfficer ||
          value.inclusionSupportPrograms ||
          value.flexibleSupportPackages ||
          value.schoolReadinessFunding ||
          value.earlyChildhoodInterventionServices,
      ],
    },

    {
      type: "field",
      fieldType: "textarea",
      key: "otherPrograms",
      label: "Please specify the other program/s you have received support from this year",
      hideExpression: (formContext, values) => !values.otherSupport.supportFromOtherProgramsOther,
      validation: Yup.string().trim().required("A response is required"),
    },
    {
      type: "field",
      fieldType: "textarea",
      key: "additionalEducators",
      label:
        "Please describe the title and role of any additional educators in attendance that assist the teaching team, such as additional assistants placed through the School Readiness Funding Program or a Flexible Support Package.",
    },
  ],
};
